import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosInstance";

function InfoItem({ label, value }) {
  return (
    <div className="p-3 rounded-3 border bg-light h-100">
      <small className="text-muted text-uppercase fw-bold" style={{ fontSize: 11 }}>
        {label}
      </small>
      <div className="fw-semibold mt-1">
        {Array.isArray(value) ? value.join(", ") || "Not set" : value || "Not set"}
      </div>
    </div>
  );
}

function TeacherViewModal({ teacher, onClose, onEdit }) {
  const [timetable, setTimetable] = useState([]);
  const [timetableLoading, setTimetableLoading] = useState(false);
  const [academicProfile, setAcademicProfile] = useState(null);
  const [academicLoading, setAcademicLoading] = useState(false);
  const [libraryBorrowings, setLibraryBorrowings] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  useEffect(() => {
    const loadTimetable = async () => {
      if (!teacher?.id) return;

      try {
        setTimetableLoading(true);
        const response = await axiosInstance.get(`teachers/${teacher.id}/timetable/`);
        setTimetable(response.data?.slots || []);
      } catch (error) {
        console.error(error);
        setTimetable([]);
      } finally {
        setTimetableLoading(false);
      }
    };

    loadTimetable();
  }, [teacher?.id]);

  useEffect(() => {
    const loadAcademicProfile = async () => {
      if (!teacher?.id) return;

      try {
        setAcademicLoading(true);
        const response = await axiosInstance.get(`teachers/${teacher.id}/academic-profile/`);
        setAcademicProfile(response.data || null);
      } catch (error) {
        console.error(error);
        setAcademicProfile(null);
      } finally {
        setAcademicLoading(false);
      }
    };

    loadAcademicProfile();
  }, [teacher?.id]);

  useEffect(() => {
    const loadLibraryBorrowings = async () => {
      if (!teacher?.id) return;

      try {
        setLibraryLoading(true);
        const response = await axiosInstance.get(`library/users/${teacher.id}/borrowings/`);
        setLibraryBorrowings(response.data || []);
      } catch (error) {
        console.error(error);
        setLibraryBorrowings([]);
      } finally {
        setLibraryLoading(false);
      }
    };

    loadLibraryBorrowings();
  }, [teacher?.id]);

  if (!teacher) return null;

  const allocations = academicProfile?.allocations || [];
  const assessments = academicProfile?.assessments || [];
  const resources = academicProfile?.learning_resources || [];
  const liveClasses = academicProfile?.live_classes || [];

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div className="d-flex align-items-center gap-3">
              <div className="rounded-circle text-white fw-bold d-flex align-items-center justify-content-center" style={{ width: 56, height: 56, background: "#125b2d" }}>
                {teacher.full_name?.split(" ").filter(Boolean).slice(0, 2).map((name) => name[0]).join("") || "TR"}
              </div>
              <div>
                <h5 className="modal-title fw-bold">{teacher.full_name}</h5>
                <small className="text-muted">{teacher.staff_number} · {teacher.job_title || "Teacher"}</small>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <span className={`badge ${teacher.is_active ? "text-bg-success" : "text-bg-secondary"}`}>
                {teacher.is_active ? teacher.status || "ACTIVE" : "INACTIVE"}
              </span>
              <span className="badge text-bg-light border">{teacher.department || "No department"}</span>
              <span className="badge text-bg-light border">
                {allocations.length} teaching allocation{allocations.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="row g-3">
              <div className="col-md-4"><InfoItem label="Staff Number" value={teacher.staff_number} /></div>
              <div className="col-md-4"><InfoItem label="Email" value={teacher.email} /></div>
              <div className="col-md-4"><InfoItem label="Phone" value={teacher.phone} /></div>
              <div className="col-md-4"><InfoItem label="Department" value={teacher.department} /></div>
              <div className="col-md-4"><InfoItem label="Job Title" value={teacher.job_title} /></div>
              <div className="col-md-4"><InfoItem label="Employment Date" value={teacher.employment_date} /></div>
              <div className="col-md-6"><InfoItem label="Qualification" value={teacher.qualification} /></div>
              <div className="col-md-6"><InfoItem label="Username" value={teacher.username} /></div>
              <div className="col-md-6"><InfoItem label="Subjects Taught" value={teacher.subjects_taught} /></div>
              <div className="col-md-6"><InfoItem label="Classes Assigned" value={teacher.classes_assigned} /></div>
              <div className="col-12"><InfoItem label="Address" value={teacher.address} /></div>
            </div>

            <div className="mt-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="fw-bold mb-0">Teaching Allocations</h6>
                  <small className="text-muted">
                    These are the official class-subject responsibilities from Academic Setup.
                  </small>
                </div>
                <span className="badge text-bg-light border">
                  {academicLoading ? "Loading..." : `${allocations.length} allocation${allocations.length === 1 ? "" : "s"}`}
                </span>
              </div>

              <div className="table-responsive border rounded-3">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Academic Year</th>
                      <th>Term</th>
                      <th>Class Group</th>
                      <th>Subject</th>
                      <th>Type</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {academicLoading ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-3">Loading allocations...</td>
                      </tr>
                    ) : allocations.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-3">
                          No teaching allocations found for this teacher.
                        </td>
                      </tr>
                    ) : (
                      allocations.map((allocation) => (
                        <tr key={allocation.id}>
                          <td>{allocation.academic_year_name || "-"}</td>
                          <td>{allocation.term_name || "-"}</td>
                          <td>{allocation.class_group_name || "-"}</td>
                          <td>{allocation.subject_name || "-"}</td>
                          <td>
                            <span className={`badge ${allocation.subject_type === "SPECIALIST" ? "text-bg-info" : "text-bg-success"}`}>
                              {allocation.subject_type_label || allocation.subject_type || "Subject"}
                            </span>
                          </td>
                          <td>{allocation.is_lead_teacher ? "Lead Teacher" : "Teacher"}</td>
                          <td>{allocation.is_active ? "Active" : "Inactive"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="fw-bold mb-0">Teacher Timetable</h6>
                  <small className="text-muted">Slots are loaded directly from timetable teacher assignment.</small>
                </div>
                <span className="badge text-bg-light border">
                  {timetable.length} slot{timetable.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="table-responsive border rounded-3">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Day</th>
                      <th>Period</th>
                      <th>Class Group</th>
                      <th>Subject</th>
                      <th>Room</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timetableLoading ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          Loading timetable...
                        </td>
                      </tr>
                    ) : timetable.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          No timetable slots found for this teacher.
                        </td>
                      </tr>
                    ) : (
                      timetable.map((slot) => (
                        <tr key={slot.id}>
                          <td>{slot.day_name}</td>
                          <td>
                            <div className="fw-semibold">{slot.period_name}</div>
                            <small className="text-muted">{slot.period_time}</small>
                          </td>
                          <td>{slot.class_group_name}</td>
                          <td>
                            {slot.subject_name || "Break / Lunch"}
                            {slot.subject_type_label && (
                              <small className="text-muted d-block">{slot.subject_type_label}</small>
                            )}
                          </td>
                          <td>{slot.room || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-lg-6">
                <ResponsibilityCard
                  title="Exams & Assessment Responsibility"
                  icon="bi-journal-check"
                  loading={academicLoading}
                  emptyText="No assessments assigned to this teacher yet."
                  rows={assessments.map((assessment) => ({
                    key: assessment.id,
                    title: assessment.subject_name || "Assessment",
                    subtitle: `${assessment.class_group_name || "-"} · ${assessment.session_name || "-"} · ${assessment.assessment_type_name || "-"}`,
                    badge: assessment.status || "DRAFT",
                  }))}
                />
              </div>

              <div className="col-lg-6">
                <ResponsibilityCard
                  title="E-learning Resources"
                  icon="bi-collection-play"
                  loading={academicLoading}
                  emptyText="No learning resources uploaded by or assigned to this teacher yet."
                  rows={resources.map((resource) => ({
                    key: resource.id,
                    title: resource.title,
                    subtitle: `${resource.class_group_name || "-"} · ${resource.subject_name || "-"} · ${resource.resource_type || "-"}`,
                    badge: resource.status || "DRAFT",
                  }))}
                />
              </div>

              <div className="col-12">
                <ResponsibilityCard
                  title="Live Online Classes"
                  icon="bi-camera-video"
                  loading={academicLoading}
                  emptyText="No live classes scheduled for this teacher yet."
                  rows={liveClasses.map((liveClass) => ({
                    key: liveClass.id,
                    title: liveClass.title,
                    subtitle: `${liveClass.class_group_name || "-"} · ${liveClass.subject_name || "-"} · ${formatDateTime(liveClass.scheduled_start)}`,
                    badge: liveClass.status || "SCHEDULED",
                  }))}
                />
              </div>
            </div>

            <div className="mt-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="fw-bold mb-0">Library History</h6>
                  <small className="text-muted">Borrowed books and digital library resources for this teacher.</small>
                </div>
                <span className="badge text-bg-light border">
                  {libraryBorrowings.length} record{libraryBorrowings.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="table-responsive border rounded-3">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Item</th>
                      <th>Borrowed</th>
                      <th>Due</th>
                      <th>Returned</th>
                      <th>Status</th>
                      <th>Fine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {libraryLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-3">
                          Loading library history...
                        </td>
                      </tr>
                    ) : libraryBorrowings.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-3">
                          No library borrowing history found.
                        </td>
                      </tr>
                    ) : (
                      libraryBorrowings.map((record) => (
                        <tr key={record.id}>
                          <td>
                            <div className="fw-semibold">{record.item_title}</div>
                            <small className="text-muted">{record.item_code || ""}</small>
                          </td>
                          <td>{record.borrowed_on || "-"}</td>
                          <td>{record.due_date || "-"}</td>
                          <td>{record.returned_on || "-"}</td>
                          <td><LibraryStatus value={record.status} /></td>
                          <td>{record.fine_amount || "0.00"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-light" onClick={onClose}>Close</button>
            <button className="btn btn-success" onClick={() => onEdit(teacher)}>
              <i className="bi bi-pencil-square me-2"></i>
              Edit Teacher
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResponsibilityCard({ title, icon, loading, emptyText, rows }) {
  return (
    <div className="border rounded-3 h-100">
      <div className="p-3 border-bottom bg-light d-flex align-items-center justify-content-between">
        <div>
          <h6 className="fw-bold mb-0">
            <i className={`bi ${icon} me-2`}></i>
            {title}
          </h6>
        </div>
        <span className="badge text-bg-light border">{rows.length}</span>
      </div>

      <div className="p-2">
        {loading ? (
          <div className="text-center text-muted py-3">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-muted py-3">{emptyText}</div>
        ) : (
          rows.slice(0, 8).map((row) => (
            <div key={row.key} className="d-flex justify-content-between gap-2 border-bottom py-2 px-1">
              <div>
                <div className="fw-semibold">{row.title}</div>
                <small className="text-muted">{row.subtitle}</small>
              </div>
              <span className="badge text-bg-light border align-self-start">{row.badge}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
}

function LibraryStatus({ value }) {
  const normalized = String(value || "").toUpperCase();
  const className = ["BORROWED", "RETURNED"].includes(normalized)
    ? "text-bg-success"
    : ["OVERDUE", "DAMAGED"].includes(normalized)
      ? "text-bg-warning"
      : normalized === "LOST"
        ? "text-bg-danger"
        : "text-bg-secondary";
  return <span className={`badge ${className}`}>{String(value || "-").replaceAll("_", " ")}</span>;
}

export default TeacherViewModal;
